import { useState } from "react";
import { useLocalStorageState } from "../../../../../hooks/useLocalStorageState";
import {
  INTEGRATION_SYNC_PRODUCTS_KEY,
  UNKNOWN_AUTHORIZATION_DATE,
} from "../config";
import { uiNotification } from "../../../../../services/UINotificationService";
import { useShopifyAuthDataQuery } from "../../../../../api/shopify/useShopifyAuthDataQuery";
import { useGetShopifyAuthUrlMutation } from "../../../../../api/shopify/useGetShopifyAuthUrlMutation";
import {
  AUTH_QUERY_PARAMS,
  useSaveShopifyIntegrationDataMutation,
} from "../../../../../api/shopify/useSaveShopifyIntegrationDataMutation";
import { getAuthQueryParams } from "../utilities";
import { history } from "../../../../../history";

const parseBoolean = (value) => {
  return value === "true";
};

export const useIntegration = () => {
  const authQueryParams = getAuthQueryParams();
  const isAuthorizedFromQuery = Boolean(authQueryParams);

  const [isEnabled, setIsEnabled] = useState(Boolean(authQueryParams));
  const [date, setDate] = useState(null);
  const [shopDomain, setShopDomain] = useState(authQueryParams?.shop || "");

  const [syncProducts, setSyncProducts] = useLocalStorageState(
    INTEGRATION_SYNC_PRODUCTS_KEY,
    parseBoolean(localStorage.getItem(INTEGRATION_SYNC_PRODUCTS_KEY)),
    {
      transform: parseBoolean,
      enableInit: true,
    },
  );

  const { isLoading } = useShopifyAuthDataQuery({
    onError: () => {
      uiNotification.error(
        "Unable to fetch integration data. Try again later.",
      );
    },
    onSuccess: (data) => {
      if (!isAuthorizedFromQuery) {
        setIsEnabled(data.isEnabled);
        setShopDomain(data.shopDomain || "");
        setDate(
          data.isEnabled ? data.updatedAt || UNKNOWN_AUTHORIZATION_DATE : null,
        );
        setSyncProducts(data.syncProducts);
      }
    },
  });

  const getAuthUrl = useGetShopifyAuthUrlMutation({
    onError: () => {
      uiNotification.error("Unable to get authorization URL. Try again later.");
    },
    onSuccess: (res) => {
      window.location.href = res.authUrl;
    },
  });

  const saveData = useSaveShopifyIntegrationDataMutation({
    onSuccess: () => {
      uiNotification.success("Integration settings updated.");
      if (isAuthorizedFromQuery) {
        history.replace(`${window.location.pathname}`);
      }
    },
    onError: () => {
      uiNotification.error(
        "Unable to update integration settings. Try again later.",
      );
    },
  });

  const onAuthorize = () => {
    if (!shopDomain) {
      uiNotification.error("Shop domain is required.");
      return;
    }
    getAuthUrl.mutate({ shopDomain });
  };

  const enableIntegration = () => {
    setIsEnabled(true);
  };

  const disableIntegration = () => {
    setIsEnabled(false);
    saveData
      .mutateAsync({
        isEnabled: false,
        syncProducts,
      })
      .catch(() => {
        setIsEnabled(true);
      });
  };

  return {
    status: {
      isEnabled,
      isLoading,
      isSaving: saveData.isLoading,
      isAuthorizing: getAuthUrl.isLoading,
      isAuthorized: !!date,
      isAuthorizedFromQuery,
    },
    set: {
      shopDomain: setShopDomain,
      syncProducts: setSyncProducts,
      isEnabled: (next) => {
        const next_ = typeof next === "function" ? next(isEnabled) : next;
        if (next_) {
          enableIntegration();
        } else {
          disableIntegration();
        }
      },
    },
    get: {
      date,
      shopDomain,
      syncProducts,
    },
    act: {
      authorize: onAuthorize,
      save: () => {
        return saveData.mutateAsync({
          isEnabled,
          syncProducts,
          [AUTH_QUERY_PARAMS.code]: authQueryParams?.code,
          [AUTH_QUERY_PARAMS.hmac]: authQueryParams?.hmac,
          [AUTH_QUERY_PARAMS.host]: authQueryParams?.host,
          [AUTH_QUERY_PARAMS.shop]: authQueryParams?.shop,
          [AUTH_QUERY_PARAMS.state]: authQueryParams?.state,
          [AUTH_QUERY_PARAMS.timestamp]: authQueryParams?.timestamp,
        });
      },
    },
  };
};
